import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios';
import type { AppState } from 'lib/redux'
import { CreatePaymentMethodData, PaymentMethodResult, StripeCardElement } from '@stripe/stripe-js';
import URI from 'urijs';
import { baseRESTUrl } from 'lib/utils';
import { teamClient } from 'lib/clients';

export interface SubscriptionState {
  details: {
    plan: {
      id: string;
      name: string;
      status: string; 
    };
    storage_used: number;
    bandwidth_used: number;
    payment_method: {
      card_type: string;
      last_four: string;
      month: number;
      year: number
    };
  }
}

const initialState: SubscriptionState = {
  details: {
    plan: {
      name: '',
      status: '',
      id: '',
    },
    storage_used: 0,
    bandwidth_used: 0,
    payment_method: null,
  }
};

type CreatePaymentMethod = (paymentMethodData: CreatePaymentMethodData) => Promise<PaymentMethodResult>

const create = async (dispatch: any, rejectWithValue: any, url: string, card: StripeCardElement, plan: string, createPaymentMethod: CreatePaymentMethod) => {
  const { error, paymentMethod: { id } } = await createPaymentMethod({ type: 'card', card });

    if (error) {
      return rejectWithValue('Could not create account');
    }

    const uri = new URI(window.location);
    const params = uri.search(true);

    const data = {
      payment_method: id,
      code: params?.code,
      plan,
    };

    const { status, data: subscriptionData } = await axios.post(`${url}/v1/team/subscription`, data, { withCredentials: true })
    
    if (status === 200) {
      dispatch(setSubscription(subscriptionData));
    }
    return subscriptionData;
}

export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (
    { card, plan, createPaymentMethod }: { card: StripeCardElement, plan: string, createPaymentMethod: CreatePaymentMethod}, 
    { dispatch, rejectWithValue }
  ) => {
    try {
      return await create(dispatch, rejectWithValue, baseRESTUrl(), card, plan, createPaymentMethod);
    } catch (err) {
      return rejectWithValue('Could not update billing details')
    }
  }
)

export const getSubscription = createAsyncThunk(
  'subscription/getSubscription',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await teamClient.getSubscription();
      dispatch(setSubscription(data));
      return data;
    } catch (err) {
      return rejectWithValue('Could not fetch subscription details')
    }
  }
)

export const updateSubscription = createAsyncThunk(
  'subscription/updateSubscription',
  async ({ card, createPaymentMethod }: any, { dispatch, rejectWithValue }) => {
    try {
      const { error, paymentMethod: { id } } = await createPaymentMethod({ type: 'card', card });
  
      if (error) {
        return rejectWithValue('Could not update subscription details');
      }

      const { data } = await teamClient.updateSubscription({
        payment_method: id
      });

      dispatch(setSubscription(data));
      return data;
    } catch (err) {
      return rejectWithValue('Could not update subscription details')
    }
  }
)

export const resubscribe = createAsyncThunk(
  'subscription/resubscribe',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await teamClient.updateSubscription({
        cancel: false,
      });
      dispatch(setSubscription(data));
      return data;
    } catch (err) {
      return rejectWithValue('Could not cancel subscription')
    }
  }
)

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    subscription: (state, { payload }) => void(state.details = payload),
  },
})

export const { subscription: setSubscription } = subscriptionSlice.actions;

export const selectSubscription = (state: AppState) => state.subscription.details;
